export interface PageMetadata {
  title: string;
  description: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
}

export const baseMetadata = {
  siteName: 'Aedra',
  siteUrl: 'https://aedra.co',
  defaultOgImage: '/og-image.jpg',
  twitterHandle: '@AedraHQ',
  founder: {
    name: 'Evan Sipplen',
    url: 'https://evansipplen.com',
  },
  foundingYear: 2025,
};

export const defaultKeywords = [
  'private market deal intelligence',
  'private equity research',
  'PE transaction dataset',
  'venture capital deal data',
  'M&A comps research',
  'custom private market dataset',
  'source-verified deal data',
  'private equity acquisitions research',
  'venture financing dataset',
  'transaction multiples research',
  'PE add-on acquisition data',
  'niche market deal research',
  'private market intelligence',
  'deal comps for PE',
  'custom PE dataset',
  'VC round history research',
  'data center infrastructure venture financing',
];

export const homeMetadata: PageMetadata = {
  title: 'Aedra | Private-Market Deal Intelligence',
  description:
    'Aedra builds custom, source-verified datasets on private equity, venture capital, and M&A activity. Finished research delivered in 2 to 3 weeks.',
  keywords: defaultKeywords,
  canonicalUrl: baseMetadata.siteUrl,
  ogType: 'website',
};

export const requestMetadata: PageMetadata = {
  title: 'Sign in',
  description:
    'Aedra access is invite only. If you have been invited, enter your email to receive a login link.',
  keywords: [
    'aedra sign in',
    'aedra invite only access',
    'private market research login',
    ...defaultKeywords.slice(0, 8),
  ],
  canonicalUrl: `${baseMetadata.siteUrl}/request`,
  ogType: 'website',
};

export const capabilitiesMetadata: PageMetadata = {
  title: 'Capabilities',
  description:
    'The types of private-market data Aedra builds, and why custom research often outperforms a database seat or months of analyst time.',
  keywords: [
    'private equity research capabilities',
    'venture capital deal mapping',
    'M&A transaction comps',
    'advisor and financing maps',
    'niche PE sector research',
    ...defaultKeywords.slice(0, 8),
  ],
  canonicalUrl: `${baseMetadata.siteUrl}/capabilities`,
  ogType: 'website',
};

export const aboutMetadata: PageMetadata = {
  title: 'About',
  description:
    'Aedra is a private-market research practice that builds custom, source-verified deal datasets for PE, VC, and M&A professionals.',
  keywords: [
    'about aedra',
    'private market research practice',
    'deal intelligence firm',
    'custom PE VC M&A research',
    ...defaultKeywords.slice(0, 6),
  ],
  canonicalUrl: `${baseMetadata.siteUrl}/about`,
  ogType: 'website',
};

export const privacyMetadata: PageMetadata = {
  title: 'Privacy Policy',
  description:
    'How Aedra collects, uses, and protects personal information submitted through our website and research engagements.',
  keywords: ['aedra privacy policy', 'private market research privacy'],
  canonicalUrl: `${baseMetadata.siteUrl}/privacy`,
  ogType: 'website',
};

export const cookiesMetadata: PageMetadata = {
  title: 'Cookie Policy',
  description: 'How Aedra uses cookies and similar technologies on aedra.co.',
  keywords: ['aedra cookie policy'],
  canonicalUrl: `${baseMetadata.siteUrl}/cookies`,
  ogType: 'website',
};

export const legalMetadata: PageMetadata = {
  title: 'Legal Disclosures',
  description:
    'Legal terms covering Aedra research engagements, website use, data limitations, intellectual property, and liability.',
  keywords: ['aedra legal disclosures', 'private market research terms'],
  canonicalUrl: `${baseMetadata.siteUrl}/legal`,
  ogType: 'website',
};

export const homeStructuredData = {
  '@type': 'WebSite',
  name: baseMetadata.siteName,
  url: baseMetadata.siteUrl,
  description: homeMetadata.description,
};

export const organizationStructuredData = {
  '@type': 'Organization',
  name: baseMetadata.siteName,
  url: baseMetadata.siteUrl,
  logo: `${baseMetadata.siteUrl}/logo.svg`,
  description:
    'Private-market deal intelligence: custom, source-verified datasets on private equity, venture capital, and M&A activity.',
  foundingDate: String(baseMetadata.foundingYear),
  founder: {
    '@type': 'Person',
    name: baseMetadata.founder.name,
    url: baseMetadata.founder.url,
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'sales',
    url: `${baseMetadata.siteUrl}/request`,
    availableLanguage: 'English',
  },
  sameAs: ['https://x.com/AedraHQ', 'https://linkedin.com/company/aedraco/'],
};

export const professionalServiceStructuredData = {
  '@type': 'ProfessionalService',
  name: 'Aedra Private-Market Deal Intelligence',
  url: baseMetadata.siteUrl,
  description: homeMetadata.description,
  areaServed: 'Worldwide',
  serviceType: [
    'Private equity transaction research',
    'Venture capital financing datasets',
    'M&A comps and deal intelligence',
  ],
};

export function getOpenGraphTags(metadata: PageMetadata) {
  return {
    'og:title': metadata.title,
    'og:description': metadata.description,
    'og:type': metadata.ogType || 'website',
    'og:url': metadata.canonicalUrl || baseMetadata.siteUrl,
    'og:image': metadata.ogImage || baseMetadata.defaultOgImage,
    'og:site_name': baseMetadata.siteName,
  };
}

export function getTwitterCardTags(metadata: PageMetadata) {
  return {
    'twitter:card': 'summary_large_image',
    'twitter:site': baseMetadata.twitterHandle,
    'twitter:title': metadata.title,
    'twitter:description': metadata.description,
    'twitter:image': metadata.ogImage || baseMetadata.defaultOgImage,
  };
}
